/**
 * Navigations-Schublade (Burger-Menü ☰) — schlanke, wiederverwendbare Kurs-Navigation
 * für Screens AUSSERHALB des Leseplayers (v. a. die Startseite). Damit ist das Menü
 * „von Anfang an" da (gleich nach dem Login), nicht erst, wenn man den Kurs startet.
 *
 * Enthält die Start-Sprünge (Startseite / Downloads / KI-Coach) und den Modulbaum
 * (Modulname → Eröffnungsseite, ▶ → Kapitel auf/zu, Kapitel → öffnen). Navigiert per
 * route() (Kaltsprung in den Kurs-Screen), funktioniert also von jeder Seite aus.
 *
 * Bewusst OHNE Lese-Tools (TTS-Knopfreihe, Modus-Panel, Abschnitts-Punkte) — die
 * gehören in den Kurs-Screen (Course.tsx). Beim Öffnen ist KEIN Modul aufgeklappt
 * (Auftraggeber: am Anfang nichts aufgeklappt). Nutzt die globalen Drawer-Styles.
 */
import { useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import type { Course } from "../data/types";
import { isTrainer } from "../state/session";
import { openSheet } from "../state/coach";
import { assetUrl } from "../data/assets";
import { IconMenu, IconHome, IconLibrary, IconSparkles, IconClose } from "./icons";

const modLabel = (m: { menueNr?: number | null; title: string }): string =>
  `${m.menueNr != null ? `Modul ${m.menueNr} · ` : ""}${m.title}`;

/** ☰-Knopf zum Öffnen der Navigations-Schublade (gleiches Aussehen wie im Kurs). */
export function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button class="menu-toggle" aria-label="Kursnavigation" onClick={onClick}>
      <IconMenu size={18} />
    </button>
  );
}

export function NavDrawer({
  open,
  onClose,
  course,
}: {
  open: boolean;
  onClose: () => void;
  course: Course | null;
}) {
  const { route } = useLocation();
  // Beim Öffnen ist nichts aufgeklappt; rein manuelles Auf-/Zuklappen.
  const [openMods, setOpenMods] = useState<string[]>([]);
  const toggleModule = (id: string) =>
    setOpenMods((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const go = (url: string) => {
    onClose();
    route(url);
  };
  const modules = (course?.modules ?? []).filter((m) => m.group !== "trainer" || isTrainer.value);

  return (
    <>
      {open ? <div class="drawer-backdrop" onClick={onClose} /> : null}
      <aside
        class={`drawer${open ? " open" : ""}`}
        aria-hidden={!open}
        style={`background:linear-gradient(180deg, rgba(14,42,84,.88), rgba(5,20,48,.97)), url('${assetUrl("bg14-flip-y.png")}') center / cover no-repeat, rgba(5,20,48,.98)`}
      >
        <div class="drawer-head">
          <strong style="font-family:var(--font-head);font-size:18px">Kursnavigation</strong>
          <button class="drawer-close" aria-label="Schließen" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {/* „Start"-Menü wie im Desktop-Header (Startseite + Unterpunkte). */}
        <nav class="drawer-start" aria-label="Start">
          <button type="button" class="drawer-start-link" onClick={() => go("/")}>
            <IconHome size={16} /> Startseite
          </button>
          <button type="button" class="drawer-start-link" onClick={() => go("/bibliothek")}>
            <IconLibrary size={16} /> Downloads
          </button>
          <button type="button" class="drawer-start-link" onClick={() => { onClose(); openSheet(); }}>
            <IconSparkles size={16} /> KI-Coach
          </button>
        </nav>

        {/* Navigationsbaum — Name-Button öffnet die Eröffnungsseite, Pfeil klappt
            nur die Kapitel auf/zu (Web-Muster S1). */}
        {modules.map((m) => {
          const offen = openMods.includes(m.id);
          return (
            <div key={m.id} class={`navmod${offen ? " open" : ""}`}>
              <div class="navmod-head">
                <button
                  type="button"
                  class="navmod-name"
                  title="Modul-Eröffnungsseite öffnen"
                  onClick={() => go(`/kurs?m=${encodeURIComponent(m.id)}&intro=1`)}
                >
                  {modLabel(m)}
                </button>
                <button
                  type="button"
                  class="navmod-toggle"
                  aria-label="Kapitel ein-/ausklappen"
                  aria-expanded={offen}
                  onClick={() => toggleModule(m.id)}
                >
                  <span class="chev" aria-hidden="true">▶</span>
                </button>
              </div>
              {offen ? (
                <div class="blocks">
                  {m.chapters.map((ch) => (
                    <div key={ch.id} class="navblock">
                      <button
                        type="button"
                        onClick={() => go(`/kurs?m=${encodeURIComponent(m.id)}&b=${encodeURIComponent(ch.id)}&resume=1`)}
                      >
                        <span class="dot" />
                        {ch.title}
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </aside>
    </>
  );
}
